import React, { type ComponentType } from 'react';
import type {
	ExecutionResult,
	MutationFunctionOptions,
	// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
} from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
import type { DataProxy } from 'apollo-cache';
import type { FetchResult } from 'apollo-link';

import type { AnnotationInfo } from '@atlaskit/editor-plugin-annotation/types';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { doc } from '@atlaskit/adf-utils/doc';
import { mention } from '@atlaskit/adf-utils/mention';
import { paragraph as p } from '@atlaskit/adf-utils/paragraph';
import { text } from '@atlaskit/adf-utils/text';
import type { DocNode } from '@atlaskit/adf-schema/doc';

import type {
	MutationFunctionInterop,
	MutationUpdaterInterop,
	// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
} from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import {
	REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import type { EditorCommentType } from '@confluence/comment/entry-points/commentTypes.ts';
import { isOptimisticSaveEnabledForTypeAndMode } from '@confluence/comment/entry-points/draftUtils.ts';
import {
	type AddCommentInCacheProps,
	type CommentInlineLocation,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { getOptimisticResponseForInlineReply } from '@confluence/comments-data-manager/entry-points/useCreateInlineReplyMutation.ts';
import {
	ADF_COMMENT_CONTENT_REPRESENTATION,
	WEB_COMMENT_SOURCE,
	addEmptyObjectRepliesAndReactions,
	getOptimisticResponseForObjectComment,
} from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutation.ts';
import {
	type ContentRepresentation as ObjectContentRepresentation,
	type Platform,
	type useCreateObjectCommentMutation$data,
	type useCreateObjectCommentMutation$variables,
} from '@confluence/comments-data-manager/entry-points/useCreateObjectCommentMutationTypes.ts';
import type { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import {
	handleInlineAnnotationNavigation,
	handleFooterCommentsNavigation,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import {
	handleMutationFailure,
	handleCreateReplySuccess as handleCreateInlineReplySuccess,
	getCommentCreationLocation,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { handleCreateReplySuccess as handleCreateGeneralReplySuccess } from '@confluence/page-comments-queries/entry-points/pageCommentUtils.ts';
// Keep the runtime enum separate because some consumers fully mock commentDataManagerUtils.
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { PassThroughProps } from '@confluence/comment/entry-points/CommentBatchList.ts';
import type {
	CommentData,
	ReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { ViewReplyOptions } from '@confluence/comments-common/entry-points/repliesHelpers.ts';
import type { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import type {
	ReplyToContributorUser as CommentsPanelReplyToContributorUser,
	ReplyToContributorUsers as CommentsPanelReplyToContributorUsers,
	ReplyToCommentUser,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import type { CommentContributor } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import type { ContentTypesCommentService } from '@confluence/content-types-bridge-comments';
import { expValEquals } from '@confluence/feature-experiments';
import {
	type CommentCreationLocation,
	ContentRepresentation as InlineContentRepresentation,
} from '@confluence/inline-comments-queries/entry-points/CreateInlineCommentMutation.ts';

import type {
	useCreateInlineReplyMutation$data,
	useCreateInlineReplyMutation$variables,
} from '@confluence/comments-data-manager/entry-points/useCreateInlineReplyMutationTypes.ts';

export type ReplyToContributorUser = CommentsPanelReplyToContributorUser;
export type ReplyToContributorUsers = CommentsPanelReplyToContributorUsers;

export const DEFAULT_NUM_OF_VISIBLE_REPLIES_FOR_RESOLVED_OR_DANGLING_COMMENT = 0;
export const DEFAULT_NUM_VISIBLE_REPLIES = 2;
export const STANDALONE_SHOW_ALL_REPLIES_CUTOFF = 5;

type HandleCommentThreadSelectionProps = {
	parentComment: CommentData;
	pageMode: PageMode;
	setCurrentlySelectedThreadKey: (
		parentCommentMarkerRef: string | undefined,
		commentType?: CommentType,
	) => void;
	event?: React.MouseEvent;
	handleSelectThread?: (threadId?: string) => void;
	/** The thread's highlighted text has been deleted, so it has no mark in the document. */
	isDetached?: boolean;
};

type HandlePanelSelectionAndScrollProps = {
	threadKey: string | null;
	isResolved: boolean;
	pageMode: PageMode;
	setCurrentlySelectedThreadKey: (
		parentCommentMarkerRef: string | undefined,
		commentType?: CommentType,
	) => void;
	commentType: CommentType;
	event?: React.MouseEvent;
	id?: string;
	/** The thread's highlighted text has been deleted, so it has no mark in the document. */
	isDetached?: boolean;
};

const shouldTriggerCommentScroll = (event: React.MouseEvent, id?: string): boolean => {
	if (!id) {
		return false;
	}
	const target = event.target as HTMLElement;
	const isContainer = target === event.currentTarget;
	const isCommentBody = Boolean(target.closest(`[data-testid="comment-thread-${id}-parent"]`));
	const isCommentReplyBody = Boolean(
		target.closest(`[data-testid="comment-thread-${id}-reply-list"]`),
	);
	const replyToggler = Boolean(target.closest('[data-testid="replies-toggler-container"]'));

	// Don't trigger if clicking on specific interactive elements
	const isInteractiveElement = Boolean(
		target.closest('button, a, input, textarea, [role="button"]'),
	);
	const isEmojiElement = Boolean(
		target.closest('[data-testid="emoji-add-icon"], [data-emoji-short-name]'),
	);

	const isGeneralReplyScroller = Boolean(
		target.closest('[data-testid="comments-panel-reply-scroller-container"]'),
	);
	const isFooterCommentThreadLoaded = Boolean(
		typeof document !== 'undefined' &&
		document.getElementById(`footer-comment-thread-${id}-container`),
	);

	// NOTE - handles scrolling in place of the general reply scroller when the footer comment thread is not yet in the DOM
	// This is a temporary solution until we update our data loading patterns. Once they're updated, this will always be false and can be removed.
	const scrollInPlaceOfGeneralReplyScroller =
		isGeneralReplyScroller && !isFooterCommentThreadLoaded;

	return (
		(isContainer ||
			isCommentBody ||
			isCommentReplyBody ||
			replyToggler ||
			isGeneralReplyScroller) &&
		(!isInteractiveElement || scrollInPlaceOfGeneralReplyScroller) &&
		!isEmojiElement
	);
};

/**
 * Determines how many replies should be visible by default before collapsing.
 *
 * Returns:
 * - For resolved/deleted comments (with feature flag): DEFAULT_NUM_OF_VISIBLE_REPLIES_FOR_RESOLVED_OR_DANGLING_COMMENT
 * - For standalone comments with few replies: Shows all replies up to STANDALONE_SHOW_ALL_REPLIES_CUTOFF
 * - For all other cases: DEFAULT_NUM_VISIBLE_REPLIES
 */
export const getDefaultNumVisibleReplies = (
	numberOfReplies: number,
	isStandaloneComment: boolean = false,
	isParentCommentClosedOrDangling: boolean = false,
): 0 | 2 | 5 => {
	if (isParentCommentClosedOrDangling) {
		return DEFAULT_NUM_OF_VISIBLE_REPLIES_FOR_RESOLVED_OR_DANGLING_COMMENT;
	}

	if (isStandaloneComment && numberOfReplies <= STANDALONE_SHOW_ALL_REPLIES_CUTOFF) {
		return STANDALONE_SHOW_ALL_REPLIES_CUTOFF;
	}

	return DEFAULT_NUM_VISIBLE_REPLIES;
};

export const getExperienceName = (
	isInlineComment: boolean,
): 'reply-to-inline-comment/publish' | 'reply-to-page-comment/publish' =>
	isInlineComment
		? REPLY_TO_INLINE_COMMENT_PUBLISH_EXPERIENCE
		: REPLY_TO_PAGE_COMMENT_PUBLISH_EXPERIENCE;

export const handleCommentThreadSelection = ({
	parentComment,
	pageMode,
	setCurrentlySelectedThreadKey,
	event,
	handleSelectThread,
	isDetached,
}: HandleCommentThreadSelectionProps): void => {
	const threadKey =
		parentComment.type === CommentType.INLINE
			? (parentComment.location as CommentInlineLocation).inlineMarkerRef
			: parentComment.id;
	const isResolved = !parentComment.isOpen;

	// Send a message via bridge api if applicable
	if (threadKey && handleSelectThread) {
		handleSelectThread(threadKey);
	}

	handlePanelSelectionAndScroll({
		threadKey: threadKey ?? null,
		isResolved,
		pageMode,
		setCurrentlySelectedThreadKey,
		commentType: parentComment.type,
		event,
		id: parentComment.id,
		isDetached,
	});
};

const clearDocumentAnnotationSelection = (pageMode: PageMode): void => {
	if (pageMode === PageMode.VIEW) {
		const emitter = getRendererAnnotationEventEmitter();
		emitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
		emitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
	} else {
		getEditorAnnotationEventEmitter().emit('setselectedannotation', '');
	}
};

export const handlePanelSelectionAndScroll = ({
	threadKey,
	isResolved,
	pageMode,
	setCurrentlySelectedThreadKey,
	commentType,
	event,
	id,
	isDetached = false,
}: HandlePanelSelectionAndScrollProps): void => {
	// We only scroll the document for non-resolved inline comments or page comments
	if (threadKey && !isResolved) {
		if (event && !shouldTriggerCommentScroll(event, id)) {
			setCurrentlySelectedThreadKey(threadKey);
		} else {
			setCurrentlySelectedThreadKey(threadKey, commentType);
		}
		if (commentType === CommentType.INLINE && typeof document !== 'undefined') {
			const commentElement = document.getElementById(threadKey);
			if (commentElement) {
				// Determine navigation context based on page mode
				const navigationContext =
					pageMode === PageMode.EDIT || pageMode === PageMode.LIVE
						? NavigationContext.EDITOR
						: NavigationContext.RENDERER;

				handleInlineAnnotationNavigation({
					scrollBehavior: ScrollBehavior.SMOOTH,
					context: navigationContext,
					block: 'center',
					inline: 'nearest',
					markElement: commentElement,
					annotationId: threadKey,
				});
			} else if (isDetached) {
				// A detached thread has no mark to move the document highlight to, so the previously
				// selected annotation would stay highlighted while a different thread is selected in
				// the panel. Clear it instead.
				clearDocumentAnnotationSelection(pageMode);
			}
		} else if (commentType === CommentType.GENERAL) {
			void handleFooterCommentsNavigation({
				scrollBehavior: ScrollBehavior.SMOOTH,
				block: 'center',
				inline: 'nearest',
				commentId: threadKey,
			});
		}
	}
};

type ReplyToCommentArgs = {
	parentCommentId?: string;
	user?: ReplyToCommentUser;
} & Partial<Record<'location', LocationValue>>;

export type SaveProps = {
	userId: string | null;
	displayName: string | null;
	pageId: string;
	parentCommentId: string;
	cloudId: string;
	adf: object;
	onSuccess: () => void;
	pageMode: PageMode;
	editCommentQueryId?: string;
	setCommentForEdit: React.Dispatch<React.SetStateAction<string>>;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	pageType: string;
	experienceTracker: ExperienceTrackerAPI;
	createInlineReplyFn?: (
		// `includeReactionsSummary` is a mutation variable that drives an `@include(if:)`
		// directive on the `reactionsSummary` field in the create-reply mutation — i.e. it tells
		// the server whether to return reaction data in the mutation response (a payload
		// optimization gated by the `confluence_fe_use_create_comment_exclude_reactions`
		// experiment). The `useCreateInlineReplyMutation` hook owns that experiment decision and
		// injects the flag itself, so callers here (buildInlineReplyOptions) must NOT supply it —
		// hence the `Omit`. It is not circular: the hook decides + injects; callers only provide
		// the reply content.
		options:
			| MutationFunctionOptions<
					useCreateInlineReplyMutation$data,
					Omit<useCreateInlineReplyMutation$variables, 'includeReactionsSummary'>
			  >
			| undefined,
		onCacheUpdateError: (error: Error) => void,
	) => Promise<ExecutionResult<useCreateInlineReplyMutation$data>>;
	createObjectReplyFn?: MutationFunctionInterop<
		useCreateObjectCommentMutation$data,
		useCreateObjectCommentMutation$variables
	>;
	addCommentInCommentsDataManagerCacheFn?: (props: AddCommentInCacheProps) => void;
	selectedAnnotation?: AnnotationInfo;
	threadKey: string;
	commentType: CommentType;
	searchSessionId?: string | null;
	isReactionsEnabled?: boolean;
	analyticsSource: AnalyticsSource;
	/** The parent thread's highlighted text has been deleted from the page. */
	isDangling?: boolean;
	contentTypesCommentService?: ContentTypesCommentService;
	isFooterComment?: boolean;
	setReplyToCommentId: (args_0?: ReplyToCommentArgs | undefined) => void;
	resetContentChanged: () => void;
	setReplyView: (view: ViewReplyOptions) => void;
};

const getEditorCommentType = (commentType: CommentType, pageMode: PageMode): EditorCommentType => {
	if (commentType === CommentType.INLINE) {
		return pageMode === PageMode.VIEW ? 'inline' : 'edit-inline';
	} else {
		// i.e. commentType === CommentType.GENERAL
		return 'page';
	}
};

// The 4 `build*Options` functions build mutation options for creating replies,
// split by reply kind to keep the main flow concise.

// Exported only for testing
export const buildInlineReplyOptions = ({
	pageId,
	parentCommentId,
	adf,
	isEditor,
	pageMode,
	threadKey,
	// Below are only used when both CSM and optimistic reply are enabled
	userId,
	displayName,
}: {
	pageId: string;
	parentCommentId: string;
	adf: object;
	isEditor: boolean;
	pageMode: PageMode;
	threadKey: string;
	userId?: string | null;
	displayName?: string | null;
}): {
	variables: {
		input: {
			containerId: string;
			parentCommentId: string;
			commentBody: {
				value: string;
				representationFormat: InlineContentRepresentation;
			};
			createdFrom:
				| CommentCreationLocation.EDITOR
				| CommentCreationLocation.LIVE
				| CommentCreationLocation.RENDERER;
		};
		pageId: string;
	};
	optimisticResponse: any;
} => ({
	variables: {
		input: {
			containerId: pageId,
			parentCommentId,
			commentBody: {
				value: JSON.stringify(adf),
				representationFormat: InlineContentRepresentation.ATLAS_DOC_FORMAT,
			},
			createdFrom: getCommentCreationLocation(isEditor, pageMode === PageMode.LIVE),
		},
		pageId,
	},
	optimisticResponse: isOptimisticSaveEnabledForTypeAndMode(
		getEditorCommentType(CommentType.INLINE, pageMode),
		'reply',
	)
		? getOptimisticResponseForInlineReply({
				userId: userId ?? '',
				displayName: displayName ?? '',
				adf,
				pageId,
				parentCommentId,
				parentCommentAnnotationId: threadKey,
			})
		: undefined,
});

// Exported only for testing
export const buildObjectReplyOptions = ({
	pageId,
	cloudId,
	parentCommentId,
	adf,
	addCommentInCommentsDataManagerCacheFn,
	experienceTracker,
	isInlineComment,
	userId,
	displayName,
}: {
	pageId: string;
	cloudId: string;
	parentCommentId: string;
	adf: object;
	addCommentInCommentsDataManagerCacheFn?: (props: AddCommentInCacheProps) => void;
	experienceTracker: ExperienceTrackerAPI;
	isInlineComment: boolean;
	userId: string | null;
	displayName: string | null;
}): {
	variables: {
		cloudId: string;
		input: {
			commentBody: {
				value: string;
				representationFormat: ObjectContentRepresentation;
			};
			commentSource: Platform;
			containerId: string;
			parentCommentId: string;
		};
		pageId: string;
		includeReactionsSummary: boolean;
	};
	update: (
		proxy: DataProxy,
		mutationResult: FetchResult<useCreateObjectCommentMutation$data>,
	) => void;
	optimisticResponse: any;
} => ({
	variables: {
		cloudId,
		input: {
			commentBody: {
				value: JSON.stringify(adf),
				representationFormat: ADF_COMMENT_CONTENT_REPRESENTATION,
			},
			commentSource: WEB_COMMENT_SOURCE,
			containerId: pageId,
			parentCommentId,
		},
		pageId,
		includeReactionsSummary: !expValEquals(
			'confluence_fe_use_create_comment_exclude_reactions',
			'isEnabled',
			true,
		),
	},
	update: (
		...params: Parameters<MutationUpdaterInterop<useCreateObjectCommentMutation$data>>
	): void => {
		const [_dataProxy, mutationResult] = params;
		addCommentInCommentsDataManagerCacheFn?.({
			newComment: addEmptyObjectRepliesAndReactions(mutationResult.data!.createFooterComment),
			onError: (error: Error) => {
				void handleMutationFailure({
					experienceTracker,
					experienceName: getExperienceName(isInlineComment),
					error,
					commentId: parentCommentId,
					shouldReturnError: true,
				});
			},
		});
	},
	optimisticResponse: isOptimisticSaveEnabledForTypeAndMode('page', 'reply')
		? getOptimisticResponseForObjectComment({
				userId: userId ?? '',
				displayName: displayName ?? '',
				adf,
				pageId,
				parentCommentId,
			})
		: undefined,
});

export const onSaveReply = async ({
	pageId,
	parentCommentId,
	cloudId,
	adf,
	onSuccess,
	pageMode,
	editCommentQueryId,
	setCommentForEdit,
	createAnalyticsEvent,
	pageType,
	experienceTracker,
	createInlineReplyFn,
	createObjectReplyFn, // Create reply via Comments Data Manager
	addCommentInCommentsDataManagerCacheFn, // Add comment to Comments Data Manager cache, passed in because it comes from a hook
	selectedAnnotation,
	threadKey,
	commentType,
	searchSessionId,
	analyticsSource,
	contentTypesCommentService,
	isFooterComment,
	setReplyToCommentId,
	resetContentChanged,
	setReplyView,
	userId,
	displayName,
	isDangling,
}: SaveProps): Promise<void | undefined> => {
	const isInlineComment = commentType === CommentType.INLINE;
	const isEditor = pageMode === PageMode.EDIT;

	const startExperienceName = getExperienceName(isInlineComment);
	experienceTracker.start({
		name: startExperienceName,
		timeout: ExperienceTimeout.EDITOR_PUBLISH,
	});

	let createReplyPromise = undefined;
	if (isInlineComment) {
		createReplyPromise = createInlineReplyFn?.(
			buildInlineReplyOptions({
				pageId,
				parentCommentId,
				adf,
				isEditor,
				pageMode,
				threadKey,
				userId,
				displayName,
			}),
			(error: Error) => {
				void handleMutationFailure({
					experienceTracker,
					experienceName: getExperienceName(isInlineComment),
					error,
					commentId: parentCommentId,
					shouldReturnError: true,
				});
			},
		);
	} else if (createObjectReplyFn) {
		// The createObjectReplyFn takes precedence over other non-inline (footer or general) reply
		// functions, if it was provided
		createReplyPromise = createObjectReplyFn(
			buildObjectReplyOptions({
				pageId,
				cloudId,
				parentCommentId,
				adf,
				addCommentInCommentsDataManagerCacheFn,
				experienceTracker,
				isInlineComment,
				userId,
				displayName,
			}),
		);
	}

	const isOptimisticReplyEnabled = isOptimisticSaveEnabledForTypeAndMode(
		getEditorCommentType(commentType, pageMode),
		'reply',
	);
	if (isOptimisticReplyEnabled) {
		// Immediately reset reply state before the createReplyPromise resolves
		setReplyToCommentId({ parentCommentId: undefined });
		setReplyView(ViewReplyOptions.ALL);
	}

	return (
		createReplyPromise &&
		createReplyPromise
			.then(({ data }: any) => {
				// Update content types comments if we are creating a reply on a different content type
				contentTypesCommentService?.onUpdateComment?.(threadKey, 'reply');

				if (!isOptimisticReplyEnabled) {
					// Reset reply state after successful creation - already done if optimistic reply is enabled
					setReplyToCommentId({ parentCommentId: undefined });
					setReplyView(ViewReplyOptions.ALL);
				}
				resetContentChanged();

				if (isInlineComment) {
					handleCreateInlineReplySuccess({
						onSuccess,
						editCommentQueryId,
						setCommentForEdit,
						data,
						createAnalyticsEvent,
						pageId,
						pageType,
						analyticsSource,
						commentId: parentCommentId,
						pageMode,
						selectedAnnotation,
						getInlineNodeTypes: undefined,
						experienceTracker,
						isEditor,
						isDangling,
					});
				} else {
					handleCreateGeneralReplySuccess({
						onSuccess,
						editCommentQueryId,
						setCommentForEdit,
						data,
						createAnalyticsEvent,
						pageId,
						pageType,
						analyticsSource,
						commentId: parentCommentId,
						pageMode,
						experienceTracker,
						searchSessionId,
						isFooterComment,
					});
				}
			})
			.catch((error: any) => {
				const failExperienceName = getExperienceName(isInlineComment);

				return handleMutationFailure({
					experienceTracker,
					experienceName: failExperienceName,
					error,
					commentId: parentCommentId,
					shouldReturnError: true,
				});
			})
	);
};

export const getCommentThreadsToBatch: (
	comments: CommentData[],
	passThroughProps: PassThroughProps,
	CommentThreadComponent: ComponentType<any>,
	transitionIndexWithinBatch: number | undefined,
) => React.JSX.Element[] = (
	comments,
	passThroughProps,
	CommentThreadComponent,
	transitionIndexWithinBatch,
) =>
	comments.map((parentComment, index) => {
		const shouldRenderCommentTypeSeparator = index === transitionIndexWithinBatch;

		return (
			<CommentThreadComponent
				// Note: Cannot let `key` use `parentComment.id`, which, when optimistic save
				// (COMMENTS-6792) is enabled, is the fake optimistic id first and then gets updated
				// to the actual new comment id after mutation succeeds.
				// When mutation succeeds, DOM tree is updated. If `key` stays the same, the update
				// is recognized as multiple individual "mutation:attribute" for known attributes
				// excluded from VC calculation (e.g. data-testid); if key changes, the update is
				// recognized as a replacement of the entire element tree and counted in VC.
				// Unlike `parentComment.id`, `index` is stable as long as the comment thread's
				// position on the page does not change.
				key={`comment-thread-${index}`}
				parentComment={parentComment}
				shouldRenderCommentTypeSeparator={shouldRenderCommentTypeSeparator}
				{...passThroughProps}
			/>
		);
	});

export const hasUnreadHiddenReply = (reply: ReplyData, hiddenReplies: ReplyData[]): boolean => {
	return hiddenReplies.some((hiddenReply: ReplyData) => hiddenReply.id === reply.id);
};

export const getDefaultReplyView = (
	numberOfReplies: number,
	isParentCommentResolvedOrDangling: boolean,
	isStandaloneComment: boolean = false,
): ViewReplyOptions.ALL | ViewReplyOptions.HIDDEN | ViewReplyOptions.LIMITED => {
	if (isParentCommentResolvedOrDangling) {
		return ViewReplyOptions.HIDDEN;
	}

	if (
		isStandaloneComment ||
		numberOfReplies <= getDefaultNumVisibleReplies(numberOfReplies, isStandaloneComment)
	) {
		return ViewReplyOptions.ALL;
	}

	return ViewReplyOptions.LIMITED;
};

export const getAdfForQuickReplyMention = (
	replyToCommentUser: ReplyToCommentUser | undefined,
): DocNode | undefined => {
	if (!replyToCommentUser || !replyToCommentUser.displayName || !replyToCommentUser.accountId) {
		return undefined;
	}

	return doc(
		p(
			mention({
				id: replyToCommentUser.accountId,
				text: `@${replyToCommentUser.displayName}`,
			}),
			text(' '),
		),
	);
};

export const getReplyToContributorUsers = (
	contributors: ReadonlyArray<CommentContributor> | null | undefined,
): ReplyToContributorUsers | undefined => {
	const seenAccountIds = new Set<string>();
	const contributorUsers = contributors?.reduce<ReplyToContributorUsers>((users, contributor) => {
		if (!contributor?.accountId || !contributor.displayName) {
			return users;
		}

		if (seenAccountIds.has(contributor.accountId)) {
			return users;
		}

		seenAccountIds.add(contributor.accountId);
		users.push({
			accountId: contributor.accountId,
			displayName: contributor.displayName,
		});

		return users;
	}, []);

	if (!contributorUsers?.length) {
		return undefined;
	}

	return contributorUsers;
};

export const getAdfForContributorReplyMentions = (
	commentUser: ReplyToCommentUser | undefined,
	contributorUsers: ReplyToContributorUsers | undefined,
): DocNode | undefined => {
	const mentionNodes =
		contributorUsers?.flatMap((contributorUser) => {
			if (!contributorUser.displayName || !contributorUser.accountId) {
				return [];
			}

			return [
				mention({
					id: contributorUser.accountId,
					text: `@${contributorUser.displayName}`,
				}),
				text(' '),
			];
		}) ?? [];

	if (!mentionNodes.length) {
		return undefined;
	}

	if (commentUser?.accountId && commentUser?.displayName) {
		mentionNodes.push(
			mention({
				id: commentUser.accountId,
				text: `@${commentUser.displayName}`,
			}),
			text(' '),
		);
	}

	return doc(p(...mentionNodes));
};

export const getReplyEditorContent = ({
	replyToCommentUser,
	replyToContributorUsers,
}: {
	replyToCommentUser: ReplyToCommentUser | undefined;
	replyToContributorUsers: ReplyToContributorUsers | undefined;
}): DocNode | undefined => {
	if (expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true)) {
		return (
			getAdfForContributorReplyMentions(replyToCommentUser, replyToContributorUsers) ??
			getAdfForQuickReplyMention(replyToCommentUser)
		);
	}

	return getAdfForQuickReplyMention(replyToCommentUser);
};
